// The console and chat, Quake-style.
//
//   ~     drops the console: scrollback, a command line, and history on ↑/↓.
//   T     one-line "say:" prompt.  Y  the same, to your team only.
//   Esc   closes whichever is open.
//
// Why DOM and not canvas: this is a text field. The browser's already handles
// selection, paste, IME and a caret; reimplementing that on a canvas to match the
// game's font would be a lot of work to arrive back where we started.
//
// The one rule that matters here: WHILE TYPING, THE GAME MUST NOT SEE THE KEYS.
// Otherwise saying "wasd" walks you off a platform. `isTyping()` is the single
// answer to that, and main.js gates every key handler on it.

const $ = (id) => document.getElementById(id);

const MAX_LINES = 400;
const COLORS = { info: "#cdd8f5", error: "#ff5b6e", echo: "#7c88a1", chat: "#ffe24a", team: "#4dffa6", frag: "#ff9f6e", system: "#8ea0c8" };

export function createConsole({ send, onName, onColor, onConnect, onVolume, onGain }) {
    const el = $("console"), log = $("conlog"), input = $("conin"), prompt = $("conprompt");
    const saybar = $("saybar"), sayin = $("sayin"), saylabel = $("saylabel");

    const history = [];
    let histAt = -1;
    let sayTeam = false;
    const lines = [];

    function print(text, kind = "info") {
        for (const raw of String(text).split("\n")) {
            lines.push({ text: raw, kind });
            const div = document.createElement("div");
            div.className = "conrow";
            div.style.color = COLORS[kind] || COLORS.info;
            div.textContent = raw;
            log.append(div);
        }
        while (lines.length > MAX_LINES) { lines.shift(); log.firstChild && log.firstChild.remove(); }
        log.scrollTop = log.scrollHeight;
    }

    // --- commands ------------------------------------------------------------
    // Local ones run here; anything that touches the simulation or other players is
    // forwarded to the server, which is the only thing allowed to decide it. These
    // are the forwarded ones — the text is for `help`, and the keys are the routing,
    // so the two can't drift apart.
    const SERVER_HELP = {
        kill: "respawn (costs a frag)",
        name: "change your name",
        rooms: "list rooms on this server",
        join: "switch room",
        create: "create <mode> <name> — make a game and join it",
        players: "who's in this room",
        to: "to <name|@team|@server> <message> — targeted chat",
        set: "set <key> <value> — retune live (also: set vol:bgm|vol:sfx <0-100>, set gain:<sound> <0..1>)",
        reload: "reload tunables from disk (undoes /set)",
    };
    // Dev/debug commands: forwarded to the server like the rest, but
    // kept out of the player-facing `help` list. `terrain <x> <y>
    // <0|1>` pokes a tile to exercise the destruction/building seam —
    // remove this when edits become a real mechanic.
    const DEV_CMDS = new Set(["terrain", "give", "spawn", "spawn-list"]);
    const SERVER_CMDS = new Set([...Object.keys(SERVER_HELP), ...DEV_CMDS]);

    const local = {};
    const def = (name, help, fn) => { local[name] = { help, fn }; };

    def("help", "list commands", () => {
        const all = [
            ...Object.entries(local).map(([n, c]) => [n, c.help]),
            ...Object.entries(SERVER_HELP),
        ];
        print("commands:", "system");
        for (const [n, h] of all.sort((a, b) => a[0].localeCompare(b[0]))) print(`  ${n.padEnd(10)} ${h}`, "echo");
        print("  ~ console · T say · Y team say · Tab scores", "echo");
    });
    def("clear", "clear the console", () => { lines.length = 0; log.replaceChildren(); });
    def("echo", "print its arguments", (args) => print(args.join(" "), "echo"));
    def("color", "set your colour (#rrggbb)", (args) => {
        if (!args[0]) return print("usage: color #39d1ff", "error");
        onColor(args[0]);
        print(`colour set to ${args[0]} — takes effect when you rejoin`, "info");
    });
    def("connect", "connect to another server", (args) => {
        if (!args[0]) return print("usage: connect host:port", "error");
        onConnect(args[0]);
    });
    def("say", "talk to the room", (args) => {
        const text = args.join(" ");
        if (text) send({ t: "chat", text, team: false });
    });
    def("say_team", "talk to your team", (args) => {
        const text = args.join(" ");
        if (text) send({ t: "chat", text, team: true });
    });

    function run(line) {
        const text = line.trim();
        if (!text) return;
        // A leading / is optional in the console and required in
        // chat, so the same parser serves both and "say /help"
        // doesn't become a command by accident.
        const bare = text.startsWith("/") ? text.slice(1) : text;
        const [cmd, ...args] = bare.split(/\s+/);
        // `set vol:bgm N` / `set vol:sfx N` tune the (client-side) audio buses, so
        // they're handled here rather than forwarded to the server like other /set.
        if (cmd === "set" && args[0] && args[0].startsWith("vol:")) {
            const bus = args[0].slice(4);
            if (bus !== "bgm" && bus !== "sfx") return print(`usage: set vol:bgm <0-100>  ·  set vol:sfx <0-100>`, "error");
            if (args[1] == null) return print(`${bus} volume is ${onVolume ? onVolume(bus, null) : "?"}`, "info");
            const n = Number(args[1]);
            if (!Number.isFinite(n)) return print(`usage: set vol:${bus} <0-100>`, "error");
            const set = onVolume ? onVolume(bus, n) : null;
            return print(`${bus} volume set to ${set}`, "info");
        }
        // `set gain:<sound> <0..1>` trims one sound's loudness live (a session tweak;
        // bake the value you like into data/audio.json). Accepts a weapon name too.
        if (cmd === "set" && args[0] && args[0].startsWith("gain:")) {
            const sound = args[0].slice(5);
            if (!sound) return print(`usage: set gain:<sound> <0..1>   (e.g. set gain:shoop 0.1)`, "error");
            if (args[1] == null) return print(`${sound} gain is ${onGain ? onGain(sound, null) : "?"}`, "info");
            const n = Number(args[1]);
            if (!Number.isFinite(n)) return print(`usage: set gain:${sound} <0..1>`, "error");
            const res = onGain ? onGain(sound, n) : null;
            if (res == null) return print(`unknown sound "${sound}"`, "error");
            return print(`${sound} gain set to ${res}`, "info");
        }
        if (local[cmd]) return local[cmd].fn(args);
        if (SERVER_CMDS.has(cmd)) {
            if (cmd === "name" && args[0]) onName(args[0]);
            return send({ t: "cmd", cmd, args });
        }
        print(`unknown command: ${cmd}  (try "help")`, "error");
    }

    // --- open/close ----------------------------------------------------------
    const isOpen = () => !el.classList.contains("hidden");
    const isSaying = () => !saybar.classList.contains("hidden");

    function open() {
        el.classList.remove("hidden");
        input.value = "";
        input.focus();
    }
    function close() {
        el.classList.add("hidden");
        input.blur();
    }
    function toggle() { isOpen() ? close() : open(); }

    function openSay(team) {
        sayTeam = team;
        saylabel.textContent = team ? "say (team):" : "say:";
        saylabel.style.color = team ? COLORS.team : COLORS.chat;
        saybar.classList.remove("hidden");
        sayin.value = "";
        sayin.focus();
    }
    function closeSay() {
        saybar.classList.add("hidden");
        sayin.blur();
    }

    // The gate main.js checks before it lets a key reach the game.
    const isTyping = () => isOpen() || isSaying();

    // --- key handling --------------------------------------------------------
    input.addEventListener("keydown", (e) => {
        e.stopPropagation(); // never let gameplay see what's being typed
        if (e.key === "Enter") {
            const line = input.value;
            input.value = "";
            if (line.trim()) {
                print("] " + line, "echo");
                history.unshift(line);
                histAt = -1;
                try { run(line); } catch (err) { print(String(err && err.message || err), "error"); }
            }
        } else if (e.key === "Escape") {
            close();
        } else if (e.key === "ArrowUp") {
            if (histAt + 1 < history.length) input.value = history[++histAt];
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            input.value = histAt > 0 ? history[--histAt] : (histAt = -1, "");
            e.preventDefault();
        } else if (e.key === "Tab") {
            // complete on the command names we know
            const stem = input.value.replace(/^\//, "");
            const all = [...Object.keys(local), ...SERVER_CMDS];
            const hits = all.filter((c) => c.startsWith(stem));
            if (hits.length === 1) input.value = hits[0] + " ";
            else if (hits.length > 1) print(hits.join("  "), "echo");
            e.preventDefault();
        }
    });

    sayin.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
            const text = sayin.value.trim();
            closeSay();
            if (!text) return;
            // A / in the say bar runs a command instead — the Quake
            // habit of typing "/kill" without opening the console.
            if (text.startsWith("/")) return run(text);
            send({ t: "chat", text, team: sayTeam });
        } else if (e.key === "Escape") {
            closeSay();
        }
    });

    print("Meme Wars console — type `help`.  ~ closes it.", "system");

    return { print, toggle, open, close, openSay, isOpen, isTyping, run };
}
